import { useFormContext, useWatch } from 'react-hook-form';
import { useState } from 'react';

import { useTotalDepositContext } from '../../contexts/TotalDepositContext';

import type { Form } from '../../queries/schema/carFinance.schema';
import { CURRENCIES } from '../../../../types';

type UseFinanceCalculatorArgs = {
  minimumDepositValue: string;
  minimumDepositPercentage: string;
  maxFinanceAmount?: number;
};

const useFinanceCalculator = (args: UseFinanceCalculatorArgs) => {
  const { minimumDepositValue, minimumDepositPercentage, maxFinanceAmount } =
    args;
  const { control, setValue, getValues, setError, clearErrors } =
    useFormContext<Form>();

  const [isCashDepositModalOpen, setIsCashDepositModalOpen] = useState(false);
  const [isTradeInValueModalOpen, setIsTradeInValueModalOpen] = useState(false);
  const [isTotalDepositModalOpen, setIsTotalDepositModalOpen] = useState(false);
  const [isOutstandingFinanceModalOpen, setIsOutstandingFinanceModalOpen] =
    useState(false);

  const [
    cashDeposit,
    tradeInValue,
    outstandingFinance,
    price,
    minimumFinanceAmount,
    pricePerMonth,
    aprRate,
  ] = useWatch<
    Form,
    [
      'loanDetails.cashDeposit',
      'loanDetails.tradeInValue',
      'loanDetails.outstandingLoan',
      'carDetails.price',
      'loanDetails.minimumFinanceAmount',
      'loanDetails.pricePerMonth',
      'loanDetails.aprRate',
    ]
  >({
    control,
    name: [
      'loanDetails.cashDeposit',
      'loanDetails.tradeInValue',
      'loanDetails.outstandingLoan',
      'carDetails.price',
      'loanDetails.minimumFinanceAmount',
      'loanDetails.pricePerMonth',
      'loanDetails.aprRate',
    ],
  });

  const { isValidDeposit } = useTotalDepositContext();

  const formatPrice = (value: number) =>
    new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: CURRENCIES.EUR,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(value);

  const formattedMinimumDepositValue = formatPrice(
    parseInt(minimumDepositValue),
  );

  const maxDepositValue = (price ?? 0) - minimumFinanceAmount;
  const formattedMaxDepositValue = formatPrice(maxDepositValue);

  const onChangeHandlerFinanceInputs = (args: {
    cashDeposit: number | undefined;
    tradeInValue: number | undefined;
    outstandingFinance: number | undefined;
  }) => {
    const { cashDeposit = 0, tradeInValue = 0, outstandingFinance = 0 } = args;

    const totalDeposit = cashDeposit + tradeInValue - outstandingFinance;
    const financeAmount = (price ?? 0) - totalDeposit;

    /**
     * We set these values of totalDeposit and financeAmount manually, rather than using derived state, for the values for these fields
     * to ensure that the correct values associated with the state of each of these fields are included in the submission.
     *
     * While the input will behave as expected, the state for both of these fields will not be updated and so will remain empty on submit.
     */
    setValue('loanDetails.totalDeposit', totalDeposit);
    setValue('loanDetails.financeAmount', Math.max(financeAmount ?? 0, 0));

    /**
     * We alert the user when the input changes rather than on submit to make it clear immediately that something is wrong,
     * before they proceeding any further in the application process.
     *
     * The finance calculator is unique in that there is no visual indication the panel is complete, unlike the other panels.
     * Both validations target cashDeposit as it's the field the user can directly increase or decrease.
     */
    if (totalDeposit < Number(minimumDepositValue)) {
      setError('loanDetails.cashDeposit', {
        message: `Total deposit must be at least <strong>${formattedMinimumDepositValue} (${minimumDepositPercentage}%)</strong>`,
      });
    } else if (financeAmount < minimumFinanceAmount) {
      setError('loanDetails.cashDeposit', {
        message: `Total deposit cannot exceed <strong>${formattedMaxDepositValue}</strong>`,
      });
    } else if (
      maxFinanceAmount !== undefined &&
      financeAmount > maxFinanceAmount
    ) {
      setError('loanDetails.cashDeposit', {
        message: `Finance amount cannot exceed <strong>${formatPrice(
          maxFinanceAmount,
        )}</strong>. Please increase your deposit.`,
      });
    } else {
      clearErrors([
        'loanDetails.totalDeposit',
        'loanDetails.cashDeposit',
        'loanDetails.financeAmount',
        'loanDetails.outstandingLoan',
        'loanDetails.tradeInValue',
      ]);
    }
  };

  const onBlurFinanceInputs = () => () => {
    const currentTotalDeposit = getValues('loanDetails.totalDeposit');
    const currentFinanceAmount = getValues('loanDetails.financeAmount');
    if (currentTotalDeposit < Number(minimumDepositValue)) {
      setError('loanDetails.cashDeposit', {
        message: `Total deposit must be at least <strong>${formattedMinimumDepositValue} (${minimumDepositPercentage}%)</strong>`,
      });
    } else if (currentFinanceAmount < minimumFinanceAmount) {
      setError('loanDetails.cashDeposit', {
        message: `Total deposit cannot exceed <strong>${formattedMaxDepositValue}</strong>`,
      });
    } else if (
      maxFinanceAmount !== undefined &&
      currentFinanceAmount > maxFinanceAmount
    ) {
      setError('loanDetails.cashDeposit', {
        message: `Finance amount cannot exceed <strong>${formatPrice(
          maxFinanceAmount,
        )}</strong>. Please increase your deposit.`,
      });
    } else {
      clearErrors([
        'loanDetails.totalDeposit',
        'loanDetails.cashDeposit',
        'loanDetails.financeAmount',
        'loanDetails.outstandingLoan',
        'loanDetails.tradeInValue',
      ]);
    }
  };

  /**
   * If price per month is 0, changing the amount of monthly repayments will not make a difference
   */
  const isMonthlyRepaymentsDisabled = !isValidDeposit || pricePerMonth === 0;

  return {
    price,
    control,
    getValues,
    formattedMinimumDepositValue,
    onChangeHandlerFinanceInputs,
    tradeInValue,
    outstandingFinance,
    cashDeposit,
    onBlurFinanceInputs,
    isCashDepositModalOpen,
    setIsCashDepositModalOpen,
    isTradeInValueModalOpen,
    setIsTradeInValueModalOpen,
    isTotalDepositModalOpen,
    setIsTotalDepositModalOpen,
    isOutstandingFinanceModalOpen,
    setIsOutstandingFinanceModalOpen,
    isMonthlyRepaymentsDisabled,
    pricePerMonth,
    aprRate,
  };
};

export { useFinanceCalculator };
